import { useState, useMemo, useEffect, type ReactNode } from "react";
import { Line } from "recharts";
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { GET_WAREHOUSE_CLUSTER_UTILIZATION_EFFICIENCY, GET_WAREHOUSE_COST } from "./constants";
import { getWarehouseClusterWarehouseUtilization, getWarehouseCostV2 } from "../../helpers/apis";
import { RelativeComponentLoader } from "../../components/loader";
import { Chart, ChartTypes } from "@/uiCore";
import { Stack } from "@altimateai/lego";
import CostsGraph from "./components/CostsGraph";
import {
  NativeSelect,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Typography,
} from "@altimateai/lego";
import type {
  CostGraphEntry,
  GetWarehouseCostParams,
  WarehouseSummaryCostGraphItem,
} from "./types";

type StackedDataEntry = Record<string, string | number | null>;

interface UtilizationEfficiencyGraphProps {
  stackedData: StackedDataEntry[];
}

const UTILIZATION_COLORS = [
  "#247EFE", // Original color
  "#FF6B6B", // Soft Red
  "#4ECDC4", // Teal
  "#F7B731", // Mustard
  "#A06CD5", // Purple
  "#45B7D1", // Sky Blue
  "#FF8C00", // Dark Orange
  "#00CED1", // Dark Turquoise
  "#FF69B4", // Hot Pink
  "#32CD32", // Lime Green
  "#FF7F50", // Coral
  "#9370DB", // Medium Purple
  "#20B2AA", // Light Sea Green
  "#FF4500", // Orange Red
  "#7B68EE", // Medium Slate Blue
  "#00FA9A", // Medium Spring Green
  "#FF1493", // Deep Pink
  "#00BFFF", // Deep Sky Blue
  "#FFD700", // Gold
  "#8A2BE2", // Blue Violet
  "#3CB371", // Medium Sea Green
  "#DC143C", // Crimson
  "#1E90FF", // Dodger Blue
  "#FF8C69", // Salmon
  "#6A5ACD", // Slate Blue
  "#2E8B57", // Sea Green
];

const UtilizationEfficiencyGraph = ({ stackedData }: UtilizationEfficiencyGraphProps) => {
  const dataKeys =
    stackedData.length > 0
      ? stackedData.reduce((acc: string[], curr) => {
          Object.keys(curr).forEach(key => {
            if (key !== "date" && !acc.includes(key)) {
              acc.push(key);
            }
          });
          return acc;
        }, [])
      : [];

  return (
    <div style={{ height: "400px", width: "100%" }}>
      <Chart
        title="Utilization Efficiency"
        xAxisDataKey="date"
        yAxisLabel={{
          value: "",
          angle: 0,
          fill: "black",
          position: "insideTopLeft",
          offset: 5,
        }}
        type={ChartTypes.LineChart}
        data={stackedData}
      >
        {({ selectedLegend }) => (
          <>
            {dataKeys.map((key, index) => {
              const color = UTILIZATION_COLORS[index % UTILIZATION_COLORS.length];
              const name =
                key === "overall_utilization_efficiency"
                  ? "Overall"
                  : `Cluster ${key.split("_").pop()}`;
              return (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={color}
                  dot={{
                    strokeWidth: 2,
                    fill: color,
                  }}
                  hide={Boolean(selectedLegend && selectedLegend !== key)}
                  name={name}
                />
              );
            })}
          </>
        )}
      </Chart>
    </div>
  );
};

interface SelectorDropdownProps {
  id?: string;
  label?: string;
  options: { label: string; value: string }[];
  method: string;
  setMethod: (value: string) => void;
}

const SelectorDropdown = ({
  id = "chart-selector",
  label = "Chart Of",
  options,
  method,
  setMethod,
}: SelectorDropdownProps) => {
  return (
    <Stack align="center">
      <Typography size="sm">{label}: </Typography>
      <NativeSelect
        id={id}
        onChange={setMethod}
        value={method}
        options={options.map(o => ({
          label: o.label,
          value: o.value,
        }))}
        buttonProps={{ "aria-label": label }}
      />
    </Stack>
  );
};

const ChartOptions = [
  { label: "Costs", value: "costs" },
  {
    label: "Utilization Efficiency",
    value: "utilization_efficiency",
  },
];

const AggregationOptions = [
  { label: "Daily", value: "day" },
  { label: "Weekly", value: "week" },
  { label: "Monthly", value: "month" },
];

interface ReportsGraphTabContentProps {
  chartType: string;
  aggregation: string;
  setAggregation: (value: string) => void;
  children: ReactNode;
}

const ReportsGraphTabContent = ({
  chartType,
  aggregation,
  setAggregation,
  children,
}: ReportsGraphTabContentProps) => {
  const chartOption = ChartOptions.find(c => c.value === chartType);
  return (
    <>
      <Stack align="center" justify="between">
        <Typography>{chartOption?.label}</Typography>
        <SelectorDropdown
          label="Aggregate"
          options={AggregationOptions}
          method={aggregation}
          setMethod={setAggregation}
        />
      </Stack>
      {children}
    </>
  );
};

interface GraphProps {
  instance_id: string;
  warehouse_rk: string;
  startDate?: Date;
  endDate?: Date;
}

const Graph = ({ instance_id, warehouse_rk, startDate, endDate }: GraphProps) => {
  const [chartType, setChartType] = useState("costs");
  const [aggregation, setAggregation] = useState("day");
  const [data, setData] = useState<WarehouseSummaryCostGraphItem[]>([]);
  const [threshold, setThreshold] = useState<number | null>(null);

  const getParams = (): GetWarehouseCostParams => {
    const params: GetWarehouseCostParams = {};
    if (startDate) params.start_date = startDate.toISOString();
    if (endDate) params.end_date = endDate.toISOString();
    if (aggregation) params.aggregation = aggregation;
    return params;
  };

  const { data: warehouseCostData, isLoading } = useQuery({
    queryKey: [GET_WAREHOUSE_COST, startDate, endDate, aggregation],
    queryFn: () => getWarehouseCostV2(instance_id, warehouse_rk, getParams()),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (warehouseCostData) {
      setData(warehouseCostData.warehouse_cost);
      setThreshold(warehouseCostData.threshold);
    }
  }, [warehouseCostData]);

  const { data: clusterEfficiencyData, isLoading: isClusterEfficiencyDataLoading } = useQuery({
    queryKey: [GET_WAREHOUSE_CLUSTER_UTILIZATION_EFFICIENCY, startDate, endDate, aggregation],
    queryFn: () => getWarehouseClusterWarehouseUtilization(instance_id, warehouse_rk, getParams()),
    enabled: chartType === "utilization_efficiency",
  });

  const formatDate = (date: string) => {
    return dayjs(date).format("DD MMM YY");
  };

  const roundToTwoDecimalPlaces = (number: number) => {
    return Math.round(number * 100) / 100;
  };

  const addPercentageInFormattedData = (formattedData: StackedDataEntry[], key: string) => {
    formattedData.forEach((entry, index) => {
      if (index === 0) return;
      const previousEntry = formattedData[index - 1];
      const previousValue = previousEntry[key] as number;
      const currentValue = entry[key] as number;

      // Handle division by zero: if previous value is 0
      if (previousValue === 0) {
        // If current value is also 0, no change (0%)
        // If current value > 0, can't calculate meaningful percentage, set to null
        entry[`percentage_${key}`] = currentValue === 0 ? 0 : null;
      } else {
        const percentage = roundToTwoDecimalPlaces(
          ((currentValue - previousValue) / previousValue) * 100
        );
        entry[`percentage_${key}`] = percentage;
      }
    });
  };
  const stackedData = useMemo((): CostGraphEntry[] => {
    if (!data || data.length === 0) return [];
    const formattedData: CostGraphEntry[] = data.map(entry => ({
      time: formatDate(entry.date),
      date: formatDate(entry.date),
      cost: roundToTwoDecimalPlaces(entry.cost),
      "cost over threshold":
        entry.cost > (threshold ?? 0) ? roundToTwoDecimalPlaces(entry.cost - (threshold ?? 0)) : 0,
      threshold: threshold ?? 0,
      "total cost": roundToTwoDecimalPlaces(entry.cost + entry.query_acceleration_cost),
      "compute cost": roundToTwoDecimalPlaces(entry.compute_cost),
      "cloud services cost": roundToTwoDecimalPlaces(entry.cloud_services_cost),
      "query acceleration cost": roundToTwoDecimalPlaces(entry.query_acceleration_cost),
      "cost under threshold":
        entry.cost > (threshold ?? 0)
          ? roundToTwoDecimalPlaces(threshold ?? 0)
          : roundToTwoDecimalPlaces(entry.cost),
    }));
    addPercentageInFormattedData(formattedData, "total cost");
    addPercentageInFormattedData(formattedData, "compute cost");
    addPercentageInFormattedData(formattedData, "cloud services cost");
    addPercentageInFormattedData(formattedData, "query acceleration cost");
    return formattedData;
  }, [data, threshold]);

  const stackedClusterEfficiencyData = useMemo((): StackedDataEntry[] => {
    if (
      !clusterEfficiencyData ||
      !clusterEfficiencyData.overall_utilization_efficiency ||
      !clusterEfficiencyData.cluster_utilization_efficiency
    ) {
      return [];
    }

    const overallData = clusterEfficiencyData.overall_utilization_efficiency;
    const clusterData = clusterEfficiencyData.cluster_utilization_efficiency;
    const clusterKeys = Object.keys(clusterData);

    const clusterMap = clusterKeys.reduce<Record<string, Record<string, number>>>(
      (acc, clusterKey) => {
        acc[clusterKey] = clusterData[clusterKey].reduce<Record<string, number>>(
          (clusterAcc, entry) => {
            clusterAcc[formatDate(entry.date)] = entry.utilization;
            return clusterAcc;
          },
          {}
        );
        return acc;
      },
      {}
    );

    return overallData.map(overall => {
      const result: StackedDataEntry = {
        date: formatDate(overall.date),
        overall_utilization_efficiency: overall.utilization,
      };

      clusterKeys.forEach(clusterKey => {
        const utilization = clusterMap[clusterKey][formatDate(overall.date)];
        if (utilization) {
          result[`utilization_efficiency_cluster_${clusterKey}`] = utilization;
        }
      });

      return result;
    });
  }, [clusterEfficiencyData]);

  if (isLoading) {
    return <RelativeComponentLoader componentHeight={40} label="Fetching warehouse cost data..." />;
  }

  if (isClusterEfficiencyDataLoading) {
    return (
      <RelativeComponentLoader
        componentHeight={40}
        label="Fetching cluster utilization efficiency data..."
      />
    );
  }

  return (
    <Stack direction="column">
      <Tabs defaultValue="costs" value={chartType} onValueChange={setChartType}>
        <TabsList className="al-grid al-w-full al-grid-cols-2">
          <TabsTrigger value="costs">Costs</TabsTrigger>
          <TabsTrigger value="utilization_efficiency">Utilization Efficiency</TabsTrigger>
        </TabsList>

        <TabsContent value="costs" className="al-mt-6">
          <ReportsGraphTabContent
            chartType={"costs"}
            aggregation={aggregation}
            setAggregation={setAggregation}
          >
            <CostsGraph
              stackedData={stackedData}
              aggregation={aggregation as "day" | "week" | "month" | undefined}
            />
          </ReportsGraphTabContent>
        </TabsContent>
        <TabsContent value="utilization_efficiency" className="al-mt-6">
          <ReportsGraphTabContent
            chartType={"utilization_efficiency"}
            aggregation={aggregation}
            setAggregation={setAggregation}
          >
            <UtilizationEfficiencyGraph stackedData={stackedClusterEfficiencyData} />
          </ReportsGraphTabContent>
        </TabsContent>
      </Tabs>
    </Stack>
  );
};

export { Graph };
